import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Modal from "@commons/Modal";
import TextInput from "@commons/Input/Text";
import PublicIcon from "@mui/icons-material/Public";
import { usePage } from "@hooks/usePage";
import { toast } from "react-toastify";
import { AppError } from "@utils/errors/appError";
import { globalMutate } from "@utils/swr";
import { createCameraTrap } from "@services/device/cameraTrap/create";
import {
  validateCoordinates,
  validateLatitude,
  validateLongitude,
} from "@utils/formatters";
import { useModalOpenWithCoordinates } from "@hooks/useModalOpenWithCoordinates";
import Heading from "@commons/Typography/Heading";
import Map from "@commons/Map";
import Text from "@commons/Typography/Text";

const cameraTrapSchema = z.object({
  number: z.string().min(1, "Número do dispositivo é obrigatório"),
  responsibleTeam: z.string().optional(),
  latitude: z
    .string({ message: "Latitude é obrigatória" })
    .min(1, "Latitude é obrigatória")
    .refine(validateLatitude(), {
      message: "Latitude deve estar entre -90 e 90",
    }),
  longitude: z
    .string({ message: "Longitude é obrigatória" })
    .min(1, "Longitude é obrigatória")
    .refine(validateLongitude(), {
      message: "Longitude deve estar entre -180 e 180",
    }),
});

type CameraTrapFormData = z.infer<typeof cameraTrapSchema>;

function ModalNewCameraTrap() {
  const { isModalOpen, closeModal, setIsLoading, setDataModal } = usePage();
  const open = isModalOpen("newCameraTrap");

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<CameraTrapFormData>({
    resolver: zodResolver(cameraTrapSchema),
    defaultValues: {
      number: "",
      responsibleTeam: "",
      latitude: undefined,
      longitude: undefined,
    },
  });

  useModalOpenWithCoordinates<CameraTrapFormData>({ open, setValue });

  const onSubmit = async (data: CameraTrapFormData) => {
    try {
      setIsLoading(true);

      await createCameraTrap({
        number: data.number,
        responsibleTeam: data.responsibleTeam || undefined,
        latitude: Number(data.latitude),
        longitude: Number(data.longitude),
      });

      toast.success("Câmera criada com sucesso");
      await globalMutate([
        "fetch-get-camera-traps",
        "fetch-camera-traps-geojson",
      ]);
      handleClose();
    } catch (error) {
      const isAppError = error instanceof AppError;
      const title = isAppError ? error.message : "Erro ao criar Câmera Trap";

      toast.error(title);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    setDataModal({});
    closeModal();
  };

  if (!open) return null;

  const latitude = watch("latitude");
  const longitude = watch("longitude");

  const { hasValidCoordinates, lat, lng } = validateCoordinates(
    latitude,
    longitude,
  );

  return (
    <Modal
      open={open}
      heading="Cadastrar uma Câmera Trap"
      onClose={handleClose}
      onConfirm={handleSubmit(onSubmit)}
      confirmLabel="Salvar"
      cancelLabel="Cancelar"
      className="max-w-2xl relative"
    >
      <form className="grid grid-cols-1 sm:grid-cols-2 text-left gap-y-4 sm:gap-6">
        <div className="col-span-2 flex gap-4 sm:gap-6 sm:flex-row flex-col">
          <Controller
            name="number"
            control={control}
            render={({ field }) => (
              <TextInput
                {...field}
                label="Número do dispositivo"
                textSize="sm"
                fullWidth
                error={!!errors.number}
                helperText={errors.number?.message}
              />
            )}
          />

          <Controller
            name="responsibleTeam"
            control={control}
            render={({ field }) => (
              <TextInput
                {...field}
                label="Equipe Responsável"
                type="text"
                textSize="sm"
                fullWidth
                error={!!errors.responsibleTeam}
                helperText={errors.responsibleTeam?.message}
              />
            )}
          />
        </div>

        <div className="col-span-2 flex gap-4 sm:gap-6 sm:flex-row flex-col">
          <Controller
            name="latitude"
            control={control}
            render={({ field }) => (
              <TextInput
                {...field}
                label="Latitude"
                textSize="sm"
                fullWidth
                type="number"
                error={!!errors.latitude}
                helperText={errors.latitude?.message}
              />
            )}
          />

          <Controller
            name="longitude"
            control={control}
            render={({ field }) => (
              <TextInput
                {...field}
                label="Longitude"
                textSize="sm"
                fullWidth
                type="number"
                error={!!errors.longitude}
                helperText={errors.longitude?.message}
              />
            )}
          />
        </div>

        <div className="h-90 w-full sm:col-span-2">
          <Heading size="sm" className="mb-4">
            Visualização das coordenadas
          </Heading>

          {hasValidCoordinates ? (
            <Map.Root>
              <Map.Localization latitude={lat} longitude={lng} draw />
            </Map.Root>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-gray-400 bg-slate-100 rounded-md shadow-sm gap-2 p-6 text-center">
              <PublicIcon className="text-4xl" />
              <Text size="sm">
                Insira coordenadas válidas para visualizar o mapa.
              </Text>
            </div>
          )}
        </div>
      </form>
    </Modal>
  );
}

export default ModalNewCameraTrap;
